import React, { useState, useEffect } from 'react';
import { FaPlus } from 'react-icons/fa';
import { fetchAPI } from '../../utils/api';

// Sub-components
import ClassStats from '../../components/admin/ClassStats';
import ClassFilter from '../../components/admin/ClassFilter';
import ClassTable from '../../components/admin/ClassTable';
import ClassForm from '../../components/admin/ClassForm';
import ContentManager from '../../components/admin/ContentManager';
import MonthModal from '../../components/admin/MonthModal';
import LessonModal from '../../components/admin/LessonModal';

export default function AdminClasses() {
    const [classes, setClasses] = useState([]);
    const [teachers, setTeachers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [view, setView] = useState('list'); // 'list', 'edit', 'content'
    const [currentClass, setCurrentClass] = useState(null);
    const [classContent, setClassContent] = useState([]);
    const [contentLoading, setContentLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedGrade, setSelectedGrade] = useState('All');
    const [status, setStatus] = useState({ type: '', text: '' });

    // Lesson Modal State
    const [lessonModal, setLessonModal] = useState({
        isOpen: false,
        monthId: null,
        parentId: null,
        data: { title: '', recording_url: '', pdf_url: '', duration: '' }
    });

    // Month Modal State
    const [monthModal, setMonthModal] = useState({
        isOpen: false,
        data: { id: null, name: '', sort_order: '' }
    });
    const [formData, setFormData] = useState({
        teacher_id: '', name: '', grade: '', subject: '', fee: '', type: 'Physical', location: '', class_schedule: ''
    });

    const loadData = async () => {
        try {
            const [classesData, teachersData] = await Promise.all([
                fetchAPI('/admin/get_classes.php'),
                fetchAPI('/admin/get_teachers.php')
            ]);
            setClasses(classesData);
            setTeachers(teachersData);
        } catch (err) {
            console.error("Failed to load data", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
    }, []);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const payload = currentClass ? { ...formData, id: currentClass.id } : formData;
            await fetchAPI('/admin/save_class.php', {
                method: 'POST',
                body: JSON.stringify(payload)
            });
            setStatus({ type: 'success', text: currentClass ? 'Class updated successfully' : 'Class created successfully' });
            setTimeout(() => setStatus({ type: '', text: '' }), 5000);
            setView('list');
            loadData();
        } catch (err) {
            setStatus({ type: 'error', text: "Error: " + err.message });
        }
    };

    const handleEdit = (cls) => {
        setCurrentClass(cls);
        setFormData({
            teacher_id: cls.teacher_id,
            name: cls.name,
            grade: cls.grade,
            subject: cls.subject,
            fee: cls.fee,
            type: cls.type,
            location: cls.location,
            class_schedule: cls.class_schedule
        });
        setView('edit');
    };

    const handleManageContent = async (cls) => {
        setCurrentClass(cls);
        setView('content');
        loadClassContent(cls.id);
    };

    const loadClassContent = async (classId) => {
        setContentLoading(true);
        try {
            const data = await fetchAPI(`/admin/get_class_content.php?class_id=${classId}`);
            setClassContent(data);
        } catch (err) {
            console.error("Failed to load content", err);
        } finally {
            setContentLoading(false);
        }
    };

    const handleAddMonth = () => {
        setMonthModal({
            isOpen: true,
            data: { id: null, name: '', sort_order: classContent.length + 1 }
        });
    };

    const handleEditMonth = (month) => {
        setMonthModal({
            isOpen: true,
            data: { ...month }
        });
    };

    const handleSaveMonth = async (e) => {
        e.preventDefault();
        try {
            await fetchAPI('/admin/save_month.php', {
                method: 'POST',
                body: JSON.stringify({ ...monthModal.data, class_id: currentClass.id })
            });
            setMonthModal({ ...monthModal, isOpen: false });
            setStatus({ type: 'success', text: 'Month saved successfully' });
            setTimeout(() => setStatus({ type: '', text: '' }), 3000);
            loadClassContent(currentClass.id);
        } catch (err) {
            setStatus({ type: 'error', text: err.message });
        }
    };

    const handleDeleteMonth = async (id) => {
        if (!window.confirm("Delete this month and all its lessons?")) return;
        try {
            await fetchAPI('/admin/delete_month.php', {
                method: 'POST',
                body: JSON.stringify({ id })
            });
            setStatus({ type: 'success', text: 'Month deleted' });
            setTimeout(() => setStatus({ type: '', text: '' }), 3000);
            loadClassContent(currentClass.id);
        } catch (err) {
            setStatus({ type: 'error', text: err.message });
        }
    };

    const handleAddLesson = (monthId, parentId = null) => {
        setLessonModal({
            isOpen: true,
            monthId,
            parentId,
            data: { title: '', recording_url: '', pdf_url: '', duration: '', display_order: 0 }
        });
    };

    const handleUpdateLesson = (lesson) => {
        setLessonModal({
            isOpen: true,
            monthId: lesson.month_id,
            parentId: lesson.parent_id,
            data: { ...lesson }
        });
    };

    const handleSaveLesson = async (e) => {
        e.preventDefault();
        try {
            await fetchAPI('/admin/save_lesson.php', {
                method: 'POST',
                body: JSON.stringify({
                    ...lessonModal.data,
                    month_id: lessonModal.monthId,
                    parent_id: lessonModal.parentId
                })
            });
            setLessonModal({ ...lessonModal, isOpen: false });
            setStatus({ type: 'success', text: 'Lesson saved successfully' });
            setTimeout(() => setStatus({ type: '', text: '' }), 3000);
            loadClassContent(currentClass.id);
        } catch (err) {
            setStatus({ type: 'error', text: err.message });
        }
    };

    const handleDeleteLesson = async (id) => {
        if (!window.confirm("Delete this lesson?")) return;
        try {
            await fetchAPI('/admin/delete_lesson.php', {
                method: 'POST',
                body: JSON.stringify({ id })
            });
            setStatus({ type: 'success', text: 'Lesson deleted' });
            setTimeout(() => setStatus({ type: '', text: '' }), 3000);
            loadClassContent(currentClass.id);
        } catch (err) {
            setStatus({ type: 'error', text: err.message });
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this class? All payments and lessons will be affected.')) return;
        try {
            await fetchAPI('/admin/delete_class.php', {
                method: 'POST',
                body: JSON.stringify({ id })
            });
            setStatus({ type: 'success', text: 'Class deleted successfully' });
            setTimeout(() => setStatus({ type: '', text: '' }), 5000);
            loadData();
        } catch (err) {
            setStatus({ type: 'error', text: "Error: " + err.message });
        }
    };

    if (loading) return <div className="p-8 text-center text-gray-500 font-bold">Loading classes...</div>;

    if (view === 'edit') {
        return (
            <ClassForm
                currentClass={currentClass}
                teachers={teachers}
                formData={formData}
                onChange={handleChange}
                onSubmit={handleSubmit}
                onCancel={() => setView('list')}
            />
        );
    }

    if (view === 'content') {
        return (
            <>
                <ContentManager
                    classContent={classContent}
                    loading={contentLoading}
                    onAddMonth={handleAddMonth}
                    onEditMonth={handleEditMonth}
                    onDeleteMonth={handleDeleteMonth}
                    onAddLesson={handleAddLesson}
                    onUpdateLesson={handleUpdateLesson}
                    onDeleteLesson={handleDeleteLesson}
                    onBack={() => setView('list')}
                />

                <MonthModal
                    isOpen={monthModal.isOpen}
                    data={monthModal.data}
                    onDataChange={(data) => setMonthModal({ ...monthModal, data })}
                    onClose={() => setMonthModal({ ...monthModal, isOpen: false })}
                    onSubmit={handleSaveMonth}
                />

                <LessonModal
                    isOpen={lessonModal.isOpen}
                    data={lessonModal.data}
                    onDataChange={(data) => setLessonModal({ ...lessonModal, data })}
                    onClose={() => setLessonModal({ ...lessonModal, isOpen: false })}
                    onSubmit={handleSaveLesson}
                />
            </>
        );
    }

    return (
        <div className="space-y-6 animated fadeIn">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 bg-white/60 backdrop-blur-xl rounded-xl p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-white/50">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 tracking-tight font-display mb-2">
                        Class Management
                    </h1>
                    <p className="text-gray-500 font-medium">
                        Manage your educational programs and content
                    </p>
                </div>
                <button
                    onClick={() => { setView('edit'); setCurrentClass(null); setFormData({ teacher_id: '', name: '', grade: '', subject: '', fee: '', type: 'Physical', location: '', class_schedule: '' }); }}
                    className="bg-gray-900 text-white px-5 py-2.5 rounded-xl font-bold hover:bg-gray-800 transition-all flex items-center gap-2 shadow-lg shadow-gray-200 active:scale-95"
                >
                    <FaPlus /> Add New Class
                </button>
            </div>

            {status.text && (
                <div className={`p-4 rounded-xl flex items-center gap-3 animate-fade-in ${status.type === 'success' ? 'bg-green-50 text-green-700 border border-green-100' : 'bg-red-50 text-red-700 border border-red-100'}`}>
                    <div className={`w-2 h-2 rounded-full ${status.type === 'success' ? 'bg-green-500' : 'bg-red-500'}`}></div>
                    <p className="text-sm font-bold">{status.text}</p>
                </div>
            )}

            <ClassStats classes={classes} teachers={teachers} />

            <ClassFilter
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                selectedGrade={selectedGrade}
                setSelectedGrade={setSelectedGrade}
            />

            <ClassTable
                classes={classes}
                searchTerm={searchTerm}
                selectedGrade={selectedGrade}
                onManageContent={handleManageContent}
                onEdit={handleEdit}
                onDelete={handleDelete}
            />
        </div>
    );
}
